import React from 'react';
import { Link } from 'react-router-dom';
import { ChatCircle, Package, Receipt, ShoppingCart } from '@phosphor-icons/react';

import { MarketShell } from '../../design-system/components/shells';
import {
  Button,
  Checkbox,
  Empty,
  Options,
  OptionRow,
  PageHeader,
  SectionTitle,
  SkeletonRow,
  Spinner,
} from '../../design-system/components/desktop';
import { Icon } from '../../design-system/components/Icon';
import { CartLayout } from '../../design-system/patterns/cart';
import { routes } from '../../routes/routes';

import ShippingForm from './ShippingForm';
import BillingForm from './BillingForm';
import OrderSummary from './OrderSummary';
import FulfillmentSelector from './FulfillmentSelector';
import ProceedPaymentModal from './ProceedPaymentModal';
import InPersonConfirmModal from './InPersonConfirmModal';
import PaymentResultModal from './PaymentResultModal';
import './checkout.css';

/**
 * Checkout, the view. Pure: every input is a prop, every output is a callback.
 *
 * The container (`Checkout.jsx`) owns the cart re-hydration, the rate quote,
 * the order lock, the place-order mutation, the analytics and every redirect.
 * This file owns what a buyer sees.
 *
 * THE REDUCED SHELL, AND THE STEPPER THAT IS NOT HERE
 * ---------------------------------------------------
 * `MarketShell variant="checkout"` drops the search, the nav links and the tab
 * bar. Removing the exits from a commit flow is the standard pattern and it is
 * what `desktop/templates/checkout.html` does.
 *
 * The template also carries a three-dot stepper, "Details, Fulfilment, Review".
 * It is NOT ported, because this app's checkout is a single page, not a wizard:
 * there is no step 1, no step 3 and no way to move between them. A stepper here
 * would be three dots describing a flow that does not exist.
 *
 * SHIPPING, OPEN PRODUCT QUESTION 1
 * ---------------------------------
 * The order summary renders the server's shipping rate when there is one, and
 * the total depends on it. That contradicts the standing rule that Sleevr never
 * shows a shipping price, and resolving it is a product decision. The
 * arithmetic is preserved exactly. The full note is on `OrderSummary`.
 *
 * NO PROMO CODE INPUT. NO "SAVED FOR LATER". NO CARD FIELDS: Sleevr does not
 * take card details on the platform, so the payment section explains what
 * happens next and collects nothing.
 */
export function CheckoutView({
  loading = false,
  cartUnavailable = false,

  form,
  errors,
  handleChange,

  showFulfillment = false,
  canSelectShipping = false,
  fulfillmentSelected = 'in_person',
  onFulfillmentSelect,
  excludeDelivery = false,
  inPersonAcknowledged = false,
  onAcknowledgeChange,
  ackError = false,

  checkoutData,
  blockedListingIds,
  shippingCost = 0,
  discount = 0,

  billingConfirmed = false,
  confirmPending = false,
  hasValidAddress = false,
  listingsError = false,
  onConfirmBilling,

  placeOrderEnabled = false,
  onPlaceOrder,

  proceedOpen = false,
  onProceedClose,
  onProceed,
  placingOrder = false,

  inPersonConfirmOpen = false,
  onInPersonCancel,
  onInPersonContinue,

  resultOpen = false,
  paymentStatus,
  onResultClose,
  onPaymentSuccess,
  onPaymentFailure,
}) {
  const shell = (children) => (
    <MarketShell
      variant="checkout"
      title="Checkout"
      breadcrumbs={[
        { label: 'Marketplace', to: routes.MARKETPLACE },
        { label: 'Cart', to: '/cart' },
        { label: 'Checkout' },
      ]}
      back={{ to: '/cart', label: 'Back to cart' }}
    >
      {children}
    </MarketShell>
  );

  /* Show the shape of what is arriving rather than a spinner: the summary is a
     list of cart lines and we already know it is a list of cart lines. */
  if (loading) {
    return shell(
      <>
        <PageHeader title="Checkout" sub="Refreshing prices and stock" />
        <div aria-busy="true">
          {Array.from({ length: 3 }).map((_, index) => (
            <SkeletonRow key={index} />
          ))}
        </div>
      </>
    );
  }

  /* The handoff is gone or its items are no longer in the cart. The container
     is already redirecting to /cart; this is what the buyer sees while it does,
     instead of an empty two-column layout. */
  if (cartUnavailable) {
    return shell(
      <>
        <PageHeader title="Checkout" />
        <Empty
          icon={ShoppingCart}
          illustration="checkout-handoff-expired"
          title="There is nothing to check out"
          text="Your cart changed while you were away, so this order was cleared. Pick your items again and try once more."
          actions={
            <Button as={Link} to="/cart" size="lg">
              Back to cart
            </Button>
          }
        />
      </>
    );
  }

  return shell(
    <>
      <PageHeader
        title="Checkout"
        sub="Confirm your details and place the order. Nothing is charged on this page."
      />

      <CartLayout variant="checkout">
        <div className="dsa-checkout__form">
          <ShippingForm form={form} handleChange={handleChange} errors={errors} />

          {showFulfillment && (
            <FulfillmentSelector
              acknowledged={inPersonAcknowledged}
              onAcknowledgeChange={onAcknowledgeChange}
              highlight={ackError}
              canSelectShipping={canSelectShipping}
              selected={fulfillmentSelected}
              onSelect={onFulfillmentSelect}
            />
          )}

          {!excludeDelivery && (
            <BillingForm form={form} handleChange={handleChange} errors={errors} />
          )}

          {/* Payment, and it collects nothing. Read-only steps, not a form. */}
          <section className="dsa-checkout__section">
            <SectionTitle>Payment</SectionTitle>
            <div className="dsa-checkout__paylist">
              <div className="dsa-checkout__payrow">
                <Icon as={Receipt} />
                <p>
                  <strong>You confirm the order here.</strong> Sleevr does not take card details on
                  the platform, so there is nothing to enter.
                </p>
              </div>
              <div className="dsa-checkout__payrow">
                <Icon as={ChatCircle} />
                <p>
                  <strong>Payment is arranged after the order is confirmed.</strong> You are handed
                  to the secure payment page once you place it.
                </p>
              </div>
              <div className="dsa-checkout__payrow">
                <Icon as={Package} />
                <p>
                  <strong>Then the order is fulfilled.</strong> In person, you pay the seller when
                  you meet and have checked the cards.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* THE SUMMARY AND THE COMMIT ARE ONE COLUMN, and this wrapper is why.
            It used to be a bare `<div>` holding a sticky summary above a static
            commit block, which is the bug Junaid hit: a sticky element travels
            inside its PARENT, and this parent runs on past the summary to the
            bottom of the Place Order button. So the summary's travel range was
            exactly the height of the commit block, and at the foot of the page
            it had slid down over the button it is supposed to sit above.
            Sticking the whole column instead gives the sticky element nothing
            below it to cover, and keeps Place Order on screen throughout.
            See `.dsa-checkout__aside` in checkout.css for the metrics. */}
        <div className="dsa-checkout__aside">
          <OrderSummary
            checkoutData={checkoutData}
            blockedListingIds={blockedListingIds}
            shipping={shippingCost}
            discount={discount}
          />

          <div className="dsa-checkout__commit">
            {!excludeDelivery && (
              /* Confirming the address is what fetches the rate, so it has to
                 happen before Place Order can be enabled on the shipping path.
                 A ticked box is state, so it is accent, never an ink fill. */
              <Options>
                <OptionRow
                  control={
                    <Checkbox
                      checked={billingConfirmed}
                      disabled={confirmPending || !hasValidAddress || listingsError}
                      onChange={() => {
                        if (
                          !confirmPending &&
                          hasValidAddress &&
                          !billingConfirmed &&
                          !listingsError
                        ) {
                          onConfirmBilling();
                        }
                      }}
                    />
                  }
                >
                  <span className="dsk-option-row__body">
                    <span className="dsk-option-row__title">
                      {confirmPending ? (
                        <Spinner size="sm">Confirming</Spinner>
                      ) : billingConfirmed ? (
                        'Delivery details confirmed'
                      ) : (
                        'Confirm delivery details'
                      )}
                    </span>
                    <span className="dsk-option-row__sub">
                      Confirming checks the address and returns the rate for this order.
                    </span>
                  </span>
                </OptionRow>
              </Options>
            )}

            <Button size="lg" block disabled={!placeOrderEnabled} onClick={onPlaceOrder}>
              Place Order
            </Button>
          </div>
        </div>
      </CartLayout>

      <ProceedPaymentModal
        isOpen={proceedOpen}
        onClose={onProceedClose}
        onProceed={onProceed}
        isProcessing={placingOrder}
      />

      <InPersonConfirmModal
        isOpen={inPersonConfirmOpen}
        onClose={onInPersonCancel}
        onContinue={onInPersonContinue}
      />

      <PaymentResultModal
        isOpen={resultOpen}
        onClose={onResultClose}
        paymentStatus={paymentStatus}
        onSuccess={onPaymentSuccess}
        onFailure={onPaymentFailure}
      />
    </>
  );
}

export default CheckoutView;
